// src/pages/AccountPage.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getMe } from "../services/users.api";
import { getMyOrders } from "../services/orders.api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import { FaGem, FaWhatsapp } from "react-icons/fa";

function AccountPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

 const [user, setUser] = useState(null);
const [orders, setOrders] = useState([]);
const [accountLoading, setAccountLoading] = useState(true);
const [activeSection, setActiveSection] = useState("account");
  const ordersSectionRef = useRef(null);
  
  const TEXT = {
    EN: {
      yourAccount: "Your account",
      myAccount: "My account",
      myOrders: "My orders",
      personalInfo: "My personal information",
      addAddress: "Add my first address",
      logout: "Log out",

      contactSupport: "Contact and Support",
      contactUs: "Contact us",
      whatsapp: "WhatsApp",

      accountTitle: "My account",
      noUser: "No account found.",
      loadingAccount: "Loading your account...",
goLogin: "Sign in or create account",
startShopping: "Start shopping",
viewAllOrders: "View all orders",
      offers:
        "By logging in with us, you will get special offers, exclusive discounts, faster checkout, and a better shopping experience.",
      yourOrder: "Your order",
      placedOrders: "You have placed",
      ordersWord: "orders",
      viewOrders: "VIEW ORDERS DETAILS",
      emailFallback: "No email added yet",
      orderListTitle: "My orders",
orderNumber: "Order",
orderDate: "Date",
orderTotal: "Total",
noOrdersYet: "You have no previous orders.",
    },
    AR: {
      yourAccount: "حسابك",
      myAccount: "حسابي",
      myOrders: "طلباتي",
      personalInfo: "معلوماتي الشخصية",
      addAddress: "إضافة أول عنوان",
      logout: "تسجيل الخروج",
loadingAccount: "جاري تحميل حسابك...",
goLogin: "تسجيل الدخول أو إنشاء حساب",
startShopping: "ابدأ التسوق",
viewAllOrders: "عرض كل الطلبات",
      contactSupport: "التواصل والدعم",
      contactUs: "تواصل معنا",
      whatsapp: "واتساب",

      accountTitle: "حسابي",
      noUser: "لا يوجد حساب.",
      offers:
        "بتسجيل الدخول معنا، ستحصل على عروض خاصة وخصومات حصرية وتجربة تسوق أسرع وأسهل.",
      yourOrder: "طلباتك",
      placedOrders: "لقد قمت بإنشاء",
      ordersWord: "طلبات",
      viewOrders: "عرض تفاصيل الطلبات",
      orderListTitle: "طلباتي",
orderNumber: "الطلب",
orderDate: "التاريخ",
orderTotal: "الإجمالي",
noOrdersYet: "لا يوجد طلبات سابقة.",
      emailFallback: "لم يتم إضافة بريد إلكتروني بعد",
    },
  }[lang];

  useEffect(() => {
  let mounted = true;

  async function load() {
    try {
      setAccountLoading(true);

      const data = await getMe();

      if (!mounted) return;

      const rawPhone = data.phone || "";
      const rawEmail = data.email || "";
      const phoneLooksLikeEmail = rawPhone.includes("@");

      setUser({
        ...data,
        phone: phoneLooksLikeEmail ? "" : rawPhone,
        email: rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
      });

      const myOrders = await getMyOrders();

      if (!mounted) return;

      setOrders(Array.isArray(myOrders) ? myOrders : []);
    } catch {
      if (mounted) {
        setUser(null);
        setOrders([]);
      }
    } finally {
      if (mounted) {
        setAccountLoading(false);
      }
    }
  }

  load();

  return () => {
    mounted = false;
  };
}, []);

  const logout = () => {
    localStorage.removeItem("esill-token");
    localStorage.removeItem("esill-user");
    window.dispatchEvent(new Event("auth-changed"));
    navigate("/");
  };

  const accountItems = [
    {
      key: "account",
      label: TEXT.myAccount,
      onClick: () => setActiveSection("account"),
    },
    {
  key: "orders",
  label: TEXT.myOrders,
  onClick: () => {
    setActiveSection("orders");

    if (window.innerWidth < 768) {
      setTimeout(() => {
        ordersSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    }
  },
},
    {
      key: "personal",
      label: TEXT.personalInfo,
      onClick: () => navigate("/edit-profile"),
    },
    {
      key: "address",
      label: TEXT.addAddress,
      onClick: () => navigate("/edit-profile"),
    },
    {
      key: "logout",
      label: TEXT.logout,
      onClick: logout,
    },
  ];

  const supportItems = [
    {
      key: "contact",
      label: TEXT.contactUs,
      onClick: () => navigate("/contact"),
    },
    {
      key: "whatsapp",
      label: TEXT.whatsapp,
      icon: <FaWhatsapp className="text-[15px]" />,
      onClick: () => {
        window.open("https://wa.me/96181446356", "_blank", "noopener,noreferrer");
      },
    },
  ];

  const AccountPageSkeleton = () => (
  <div
    className="w-full min-h-screen bg-white overflow-x-hidden"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <main className="bg-white px-4 md:px-8 md:pt-[175px]">
      {/* MOBILE SKELETON */}
      <div className="md:hidden">
        <div className="pt-6 space-y-9">
          {[1, 2].map((group) => (
            <div key={group}>
              <div className="h-5 w-[140px] bg-gray-200 animate-pulse rounded mb-4" />

              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-4 w-[180px] bg-gray-200 animate-pulse rounded"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="pt-8 pb-10">
          <div className="h-9 w-[190px] bg-gray-200 animate-pulse rounded mb-4" />
          <div className="h-5 w-[160px] bg-gray-200 animate-pulse rounded mb-2" />
          <div className="h-4 w-[220px] bg-gray-200 animate-pulse rounded" />

          <div className="mt-7 rounded-2xl p-6 bg-gray-200 animate-pulse h-[150px]" />

          <div className="mt-10">
            <div className="h-7 w-[130px] bg-gray-200 animate-pulse rounded mb-4" />
            <div className="h-4 w-[210px] bg-gray-200 animate-pulse rounded mb-5" />
            <div className="h-11 w-[180px] bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
      </div>

      {/* DESKTOP SKELETON */}
      <div className="hidden md:grid grid-cols-[20%_4px_1fr] gap-10 max-w-7xl mx-auto pb-14">
        <aside className="pt-3 space-y-9">
          {[1, 2].map((group) => (
            <div key={group}>
              <div className="h-5 w-[150px] bg-gray-200 animate-pulse rounded mb-4" />

              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-4 w-[170px] bg-gray-200 animate-pulse rounded"
                  />
                ))}
              </div>
            </div>
          ))}
        </aside>

        <div className="w-[4px] bg-gray-200 min-h-[520px]" />

        <section className="pt-3">
          <div className="h-10 w-[220px] bg-gray-200 animate-pulse rounded mb-4" />
          <div className="h-5 w-[190px] bg-gray-200 animate-pulse rounded mb-2" />
          <div className="h-4 w-[260px] bg-gray-200 animate-pulse rounded" />

          <div className="mt-7 rounded-2xl p-8 bg-gray-200 animate-pulse h-[150px]" />

          <div className="mt-10">
            <div className="h-8 w-[160px] bg-gray-200 animate-pulse rounded mb-4" />
            <div className="h-5 w-[230px] bg-gray-200 animate-pulse rounded mb-5" />
            <div className="h-11 w-[210px] bg-gray-200 animate-pulse rounded" />
          </div>
        </section>
      </div>
    </main>

    <Footer />
  </div>
);

const AccountEmptyState = () => (
  <div className="max-w-[560px]">
    <h1 className="text-[30px] md:text-[38px] font-black text-black leading-tight mb-4">
      {TEXT.accountTitle}
    </h1>

    <div className="border border-gray-200 bg-white rounded-2xl p-6 md:p-8 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
      <div
        className="
          mx-auto md:mx-0
          w-[58px] h-[58px]
          rounded-full
          border border-black
          flex items-center justify-center
          text-[28px]
          text-black
          mb-5
        "
      >
        !
      </div>

      <h2 className="text-[22px] md:text-[26px] font-bold text-black">
        {TEXT.noUser}
      </h2>

      <p className="mt-3 text-[14px] md:text-[15px] leading-7 text-gray-600">
        {TEXT.offers}
      </p>

      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={() => navigate("/create-account")}
          className="
            h-11
            px-6
            border-2 border-black
            rounded-md
            bg-black
            text-white
            text-sm font-semibold tracking-wide
            hover:opacity-90
            transition
          "
        >
          {TEXT.goLogin}
        </button>

        <button
          type="button"
          onClick={() => navigate("/all-products")}
          className="
            h-11
            px-6
            border-2 border-black
            rounded-md
            bg-white
            text-black
            text-sm font-semibold tracking-wide
            hover:bg-black hover:text-white
            transition
          "
        >
          {TEXT.startShopping}
        </button>
      </div>
    </div>
  </div>
);

const OrdersEmptyState = () => (
  <div className="border border-gray-200 bg-white rounded-2xl p-6 md:p-8 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
    <div
      className="
        w-[54px] h-[54px]
        rounded-full
        border border-black
        flex items-center justify-center
        text-[26px]
        text-black
        mb-5
      "
    >
      !
    </div>

    <h2 className="text-[22px] md:text-[26px] font-bold text-black">
      {TEXT.noOrdersYet}
    </h2>

    <p className="mt-3 text-[14px] md:text-[15px] leading-7 text-gray-600">
      {lang === "AR"
        ? "عند إتمام أول طلب، ستظهر تفاصيله هنا."
        : "Once you place your first order, its details will appear here."}
    </p>

    <button
      type="button"
      onClick={() => navigate("/all-products")}
      className="
        mt-7
        h-11
        px-6
        border-2 border-black
        rounded-md
        bg-white
        text-black
        text-sm font-semibold tracking-wide
        hover:bg-black hover:text-white
        transition
      "
    >
      {TEXT.startShopping}
    </button>
  </div>
);

  const MenuList = ({ title, items }) => (
  <div className={`mb-9 ${lang === "AR" ? "text-right" : "text-left"}`}>
    <h3 className="text-[16px] font-bold text-black mb-3">
      {title}
    </h3>

    <div className="space-y-2">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={item.onClick}
          className={`
            w-full
            flex items-center gap-2
            text-[14px]
            text-gray-900
            hover:underline
            transition
            ${lang === "AR" ? "justify-start text-right" : "justify-start text-left"}
            ${activeSection === item.key ? "font-bold" : "font-normal"}
          `}
        >
          {item.icon && <span className="shrink-0">{item.icon}</span>}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  </div>
);

  const AccountContent = () => (
    <div>
      <h1 className="text-[30px] md:text-[38px] font-black text-black leading-tight mb-3">
        {TEXT.accountTitle}
      </h1>

      {!user ? (
  <AccountEmptyState />
) : (
        <>
          <p className="text-[16px] md:text-[18px] font-medium text-gray-900">
            {user.full_name}
          </p>

          <p className="md:block text-sm text-gray-600 mt-1">
            {user.email || TEXT.emailFallback}
          </p>

          {/* GOLD CARD */}
          <div
            className="
              relative
              mt-7
              rounded-2xl
              p-6 md:p-8
              overflow-hidden
              shadow-[0_18px_40px_rgba(0,0,0,0.12)]
            "
            style={{
              background:
                "linear-gradient(135deg, #c79732 0%, #f4d47a 45%, #b68122 100%)",
            }}
          >
            <FaGem
              className="
                absolute top-5 right-5
                text-black
                text-[34px]
                drop-shadow-[0_0_10px_rgba(255,255,255,0.75)]
              "
            />

            <div className="pr-12">
              <p className="text-[15px] md:text-[17px] font-semibold text-black leading-7 max-w-xl">
                {TEXT.offers}
              </p>
            </div>
          </div>

          {/* ORDERS */}
          <div ref={ordersSectionRef} className="mt-10 scroll-mt-24">
            <h2 className="text-[22px] md:text-[26px] font-bold text-black mb-3">
              {TEXT.yourOrder}
            </h2>

            <p className="text-sm md:text-base text-gray-800 mb-5">
              {TEXT.placedOrders}{" "}
              <span className="font-bold">{orders.length}</span>{" "}
              {TEXT.ordersWord}
            </p>

            <button
              type="button"
              onClick={() => navigate("/orders")}
              className="
                h-11 px-6
                border-2 border-black
                rounded-md
                bg-white
                text-black
                text-sm font-semibold tracking-wide
                hover:bg-black hover:text-white
                transition
              "
            >
              {TEXT.viewOrders}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const OrdersContent = () => (
  <div>
    <h1 className="text-[30px] md:text-[38px] font-black text-black leading-tight mb-6">
      {TEXT.orderListTitle}
    </h1>

    {orders.length === 0 ? (
  <OrdersEmptyState />
) : (
      <div className="space-y-4">
        {orders.slice(0, 5).map((order) => (
          <div
            key={order.id}
            className="
              bg-white
              border border-gray-200
              rounded-xl
              p-5
              shadow-[0_10px_24px_rgba(0,0,0,0.05)]
            "
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">
                  {TEXT.orderNumber}
                </p>

                <p className="text-[17px] font-bold text-black">
                  {order.order_number}
                </p>
              </div>

              <div className={lang === "AR" ? "text-left" : "text-right"}>
                <p className="text-sm text-gray-500">
                  {TEXT.orderDate}
                </p>

                <p className="text-sm font-medium text-gray-900">
                  {new Date(order.created_at).toLocaleDateString(
                    lang === "AR" ? "ar" : "en",
                    {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
              <span className="text-sm text-gray-600">
                {TEXT.orderTotal}
              </span>

              <span className="text-[16px] font-bold text-black">
                ${order.total_amount}
              </span>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => navigate("/orders")}
          className="
            h-11 px-6
            border-2 border-black
            rounded-md
            bg-white
            text-black
            text-sm font-semibold tracking-wide
            hover:bg-black hover:text-white
            transition
          "
        >
          {TEXT.viewOrders}
        </button>
      </div>
    )}
  </div>
);

if (accountLoading) {
  return <AccountPageSkeleton />;
}

  return (
    <div
      className="w-full min-h-screen bg-white overflow-x-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <main className="bg-white px-4 md:px-8 md:pt-[175px]">
        {/* MOBILE */}
        <div className="md:hidden">
          <div className="pt-6">
            <MenuList title={TEXT.yourAccount} items={accountItems} />
            <MenuList title={TEXT.contactSupport} items={supportItems} />
          </div>

          <div className="pt-3 pb-8">
            <AccountContent />
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden md:grid grid-cols-[20%_4px_1fr] gap-10 max-w-7xl mx-auto pb-14">
          {/* LEFT SIDEBAR */}
          <aside className="pt-3">
            <MenuList title={TEXT.yourAccount} items={accountItems} />
            <MenuList title={TEXT.contactSupport} items={supportItems} />
          </aside>

          {/* THICK VERTICAL LINE */}
          <div className="w-[4px] bg-gray-300 min-h-[520px]" />

          {/* RIGHT CONTENT */}
          <section className="pt-3">
  {activeSection === "account" && <AccountContent />}
  {activeSection === "orders" && <OrdersContent />}
</section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default AccountPage;